import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { FormControl, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { HttpClient } from '@angular/common/http';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageService } from 'primeng/api';
import { ProductService } from '../../../core/services/product.service';
import { CustomerService } from '../../../core/services/customer.service';
import { ReservationService, CreateReservationRequest } from '../../../core/services/reservation.service';
import { SubscriberService } from '../../../core/services/subscriber.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { EncryptionService } from '../../../core/services/encryption';
import { Router, ActivatedRoute } from '@angular/router';


@Component({
  selector: 'app-add-reservation',
  imports: [CommonModule, ToastModule, ReactiveFormsModule, FormsModule, CardModule, InputTextModule, SelectModule, ButtonModule, ProgressSpinnerModule, CheckboxModule],
  templateUrl: './add-reservation.html',
  styleUrl: './add-reservation.scss',
  providers: [MessageService]
})
export class AddReservation {
  loggedInUserRole: string = '';
  loggedInUserID: string = '';
  loggedInUserSubscriberId: string = '';
  loading: boolean = false;
  isFormLoading: boolean = true;
  
  reservationForm: FormGroup = new FormGroup({
    customerId: new FormControl('', [Validators.required]),
  });

  productsOptions: any[] = [];
  customerOptions: any[] = [];
  selectedProducts: any[] = [];
  selectedProduct: any = null;
  preSelectedProductIds: string[] = [];
  availableProducts: any[] = []; // Products available from customer lookup (for selection/deselection)
  
  // Track if component is loaded with pre-filled data
  isFromCustomerLookup: boolean = false;
  isCustomerPreSelected: boolean = false;
  customerName: string = '';

  constructor(
    private productService: ProductService, 
    private customerService: CustomerService, 
    private reservationService: ReservationService,
    private subscriberService: SubscriberService,
    private messageService: MessageService,
    private encryption: EncryptionService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser();
    this.loadProductsAndCustomers();
    this.handleQueryParams();
  }

  private loadProductsAndCustomers(): void {
    let productsLoaded = false;
    let customersLoaded = false;

    this.productService.getAllProducts().subscribe((products) => {
      this.productsOptions = products.map((product) => ({ label: product.name, value: product._id }));
      productsLoaded = true;
      
      // Load pre-selected products if we have product IDs from query params
      if (this.preSelectedProductIds.length > 0) {
        this.loadPreSelectedProducts();
      }
      
      this.checkIfDataLoaded(productsLoaded, customersLoaded);
    });

    this.customerService.getAllCustomers().subscribe((response: any) => {
      this.customerOptions = response.customers.map((customer: any) => ({ label: customer.businessName, value: customer._id }));
      customersLoaded = true;
      
      this.checkIfDataLoaded(productsLoaded, customersLoaded);
    });
  }

  private checkIfDataLoaded(productsLoaded: boolean, customersLoaded: boolean): void {
    if (productsLoaded && customersLoaded) {
      this.isFormLoading = false;
    }
  }

  private handleQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      if (params['customerId']) {
        // Set customer and disable the field
        this.reservationForm.patchValue({
          customerId: params['customerId']
        });
        this.reservationForm.get('customerId')?.disable();
        this.isCustomerPreSelected = true;
        this.isFromCustomerLookup = true;
      }

      if (params['productIds']) {
        const productIds = params['productIds'].split(',');
        // Store product IDs to load once products are fetched
        this.preSelectedProductIds = productIds;
        this.isFromCustomerLookup = true;
        
        // Load products immediately if they're already available
        if (this.productsOptions.length > 0) {
          this.loadPreSelectedProducts();
        }
      }

      if (params['businessName']) {
        this.customerName = params['businessName'];
        this.isFromCustomerLookup = true;
      }
    });
  }

  private loadPreSelectedProducts(): void {
    if (this.preSelectedProductIds.length > 0 && this.productsOptions.length > 0) {
      // Load all available products from the passed IDs
      this.availableProducts = this.productsOptions.filter(product => 
        this.preSelectedProductIds.includes(product.value)
      );
      // Initially select all products
      this.selectedProducts = [...this.availableProducts];
      console.log('Pre-selected products loaded:', this.selectedProducts);
    }
  }
  
  // Toggle product selection (for confirmation page checkboxes)
  toggleProductSelection(product: any): void {
    const index = this.selectedProducts.findIndex(p => p.value === product.value);
    if (index > -1) {
      // Deselect
      this.selectedProducts.splice(index, 1);
    } else {
      // Select
      this.selectedProducts.push(product);
    }
  }
  
  // Check if product is selected (for checkbox binding)
  isProductSelected(product: any): boolean {
    return this.selectedProducts.some(p => p.value === product.value);
  }
  
  // Handle checkbox change
  onProductCheckboxChange(product: any, checked: boolean): void {
    if (checked) {
      // Add product if not already selected
      if (!this.isProductSelected(product)) {
        this.selectedProducts.push(product);
      }
    } else {
      // Remove product
      const index = this.selectedProducts.findIndex(p => p.value === product.value);
      if (index > -1) {
        this.selectedProducts.splice(index, 1);
      }
    }
  }

  loadCurrentUser(): void {
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);

    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      const user = JSON.parse(userStr);
      this.loggedInUserRole =
        typeof user.role === 'object' ? user.role?.identification : user.role;
      this.loggedInUserID = user._id;
      this.loggedInUserSubscriberId =
        typeof user.subscriberId === 'object'
          ? user.subscriberId?._id
          : user.subscriberId;
    }
  }

  private async calculateExpiryDate(): Promise<Date> {
    try {
      const subscriber = await this.subscriberService.findSubscriber(this.loggedInUserSubscriberId);
      
      const actualSubscriber = subscriber.subscriber || subscriber;
      
      if (actualSubscriber && actualSubscriber.contactDuration) {
        const contactDuration = parseInt(actualSubscriber.contactDuration);
        
        if (!isNaN(contactDuration)) {
          const currentDate = new Date();
          const expiryDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + contactDuration, currentDate.getDate());
          return expiryDate;
        }
      }
    } catch (error) {
      console.error('Error fetching subscriber data for expiry calculation:', error);
    }
    
    const currentDate = new Date();
    const expiryDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, currentDate.getDate());
    return expiryDate;
  }

  // Handle product selection change
  onProductChange(event: any) {
    this.selectedProduct = event.value;
  }

  // Navigate to customer single page
  customerSinglePage(): void {
    const customerId = this.reservationForm.get('customerId')?.value;
    if (customerId) {
      this.router.navigate(['/admin/view-customer', customerId]);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Customer Selected',
        detail: 'Please select a customer first to view their details.'
      });
    }
  }

  // Handle Add button click for products (only for non-customer-lookup mode)
  addSelectedProduct() {
    if (this.isFromCustomerLookup) {
      return; // Don't allow adding products when coming from customer lookup
    }
    
    if (this.selectedProduct) {
      console.log('Selected product:', this.selectedProduct);
      console.log('Current selected products:', this.selectedProducts);
      
      // Check if product already exists by comparing IDs (using 'value' field)
      const productExists = this.selectedProducts.some(p => p.value === this.selectedProduct.value);
      console.log('Product exists check:', productExists);
      
      if (!productExists) {
        const productToAdd = this.selectedProduct;
        this.selectedProducts.push(productToAdd);
        this.selectedProduct = null; // Reset selection
        console.log('Product added successfully. Updated array:', this.selectedProducts);
        this.messageService.add({
          severity: 'success',
          summary: 'Product added',
          detail: `${productToAdd.label} has been added to the reservation`
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Product exists',
          detail: 'This product is already in the reservation'
        });
        this.selectedProduct = null; // Reset selection
      }
    }
  }

  // Remove product from the list (only for non-customer-lookup mode)
  removeProduct(product: any) {
    if (this.isFromCustomerLookup) {
      // Use toggle selection instead
      this.toggleProductSelection(product);
      return;
    }
    
    const index = this.selectedProducts.findIndex(p => p.value === product.value);
    if (index > -1) {
      this.selectedProducts.splice(index, 1);
      this.messageService.add({
        severity: 'info',
        summary: 'Product removed',
        detail: 'Product has been removed from the reservation'
      });
    }
  }

  // Create reservation
  async createReservation(): Promise<void> {
    // Check if form is valid (ignoring disabled fields for validation)
    const isFormValid = this.reservationForm.get('customerId')?.value || this.isCustomerPreSelected;
    
    if (isFormValid) {
      this.loading = true;
      
      const formData = this.reservationForm.getRawValue(); // Use getRawValue to get disabled field values
      
      // Use selected products array
      if (this.selectedProducts.length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'No Products Selected',
          detail: 'Please select at least one product for the reservation'
        });
        this.loading = false;
        return;
      }
      
      // Extract product IDs from selected products (using 'value' field)
      const products = this.selectedProducts.map(p => p.value);
      
      // Calculate expiry date based on subscriber contact duration
      const expiryDate = await this.calculateExpiryDate();
      
      const reservationData: CreateReservationRequest = {
        customerId: formData.customerId,
        products: products,
        expiryDate: expiryDate
      };

      this.reservationService.createReservation(reservationData).subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Reservation created successfully!'
          });

          // Keep products visible during navigation delay
          setTimeout(() => {
            this.loading = false;
            // Reset form and selected products only after navigation
            this.reservationForm.reset();
            this.selectedProducts = [];
            this.selectedProduct = null;
            this.router.navigate(['/admin/view-customer', formData.customerId]);
          }, 2000);
        },
        error: (error) => {
          this.loading = false;
          console.error('Error creating reservation:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error?.message || 'Failed to create reservation'
          });
        }
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields'
      });
    }
  }
}
